// src/pages/MemberFormModal.jsx
import React, { useState, useEffect } from "react";
import { X, ChevronDown, Loader2, MapPin } from "lucide-react";
import planApi from '@api/planApi'; 

const ROLES = [
  { value: "R2", label: "사용자 (수행요원)" },
  { value: "R0", label: "시스템 관리자" }
];

const DEFAULT_FORM = {
  memberId: "",
  password: "", // 🌟 비밀번호 필드 추가
  memberNm: "",
  roleId: "R2",
  departmentNm: "", 
  phone: "",
  email: "",
  status: "A",
  staffId: "",
  zipcode: "",
  address1: "",
  address2: ""
};

export function MemberFormModal({ type, member, onClose, onSubmit, isSubmitting }) {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [staffList, setStaffList] = useState([]); 
  const [isLoadingStaff, setIsLoadingStaff] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await planApi.getAllStaffForMember();
        setStaffList(res || []);
      } catch (error) {
        console.error("수행요원 목록 로드 실패", error);
      } finally {
        setIsLoadingStaff(false);
      }
    };
    fetchStaff();
  }, []);

  useEffect(() => {
    if (type === "edit" && member) {
      setFormData({
        memberId: member.memberId || "",
        password: "", // 🌟 수정 모드일 때는 비밀번호를 항상 빈칸으로 초기화 (입력 시에만 변경됨)
        memberNm: member.memberNm || "",
        roleId: member.roleId || "R2",
        departmentNm: member.branchName || "", 
        phone: member.phone || "",
        email: member.email || "",
        status: member.status || "A",
        staffId: member.staffId || "",
        zipcode: member.zipcode || "",
        address1: member.address1 || "",
        address2: member.address2 || ""
      });
    } else {
      setFormData(DEFAULT_FORM);
    }
  }, [type, member]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let phone1 = "", phone2 = "", phone3 = "";
    if (formData.phone) {
      const parts = formData.phone.replace(/[^0-9]/g, '').match(/^(\d{2,3})(\d{3,4})(\d{4})$/);
      if (parts) {
        phone1 = parts[1]; phone2 = parts[2]; phone3 = parts[3];
      }
    }

    const payload = {
      ...formData,
      staffId: formData.staffId === "" ? null : formData.staffId, 
      phone1, phone2, phone3
    };

    try {
      if (type === "add") {
        await onSubmit(payload);
        alert("회원 등록이 완료되었습니다.");
      } else {
        await onSubmit({ memberId: formData.memberId, payload });
        alert("회원 정보가 수정되었습니다.");
      }
      onClose();
    } catch (error) {
      alert("처리 중 오류가 발생했습니다: " + (error?.response?.data?.message || "알 수 없는 오류"));
    }
  };

  const openAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function(data) {
        setFormData(prev => ({
          ...prev,
          zipcode: data.zonecode,
          address1: data.address
        }));
      }
    }).open();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh] overflow-hidden">
        
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
          <h3 className="text-base font-bold text-slate-800">
            {type === "add" ? "신규 회원 등록" : "회원 정보 수정"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
            
            {/* 🌟 아이디 / 비밀번호 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">아이디 (필수)</label>
                <input
                  type="text" name="memberId" required readOnly={type === "edit"}
                  value={formData.memberId} onChange={handleChange} placeholder="agent000"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 read-only:bg-slate-50 read-only:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                  비밀번호 {type === "add" ? "(필수)" : "(선택)"}
                </label>
                <input
                  type="password" name="password" required={type === "add"}
                  value={formData.password} onChange={handleChange} 
                  placeholder={type === "add" ? "비밀번호 입력" : "변경 시에만 입력"}
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* 🌟 성명 / 역할 권한 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">성명 (필수)</label>
                <input
                  type="text" name="memberNm" required
                  value={formData.memberNm} onChange={handleChange} placeholder="홍길동"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">역할 권한</label>
                <div className="relative">
                  <select name="roleId" value={formData.roleId} onChange={handleChange} className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* 소속명 / 수행요원 맵핑 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">소속명</label>
                <input
                  type="text" name="departmentNm"
                  value={formData.departmentNm} onChange={handleChange} placeholder="소속 지사/부서"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">수행요원 맵핑 (선택)</label>
                <div className="relative">
                  <select 
                    name="staffId" value={formData.staffId} onChange={handleChange} 
                    className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    disabled={isLoadingStaff}
                  >
                    <option value="">-- 매핑 안함 --</option>
                    {staffList.map((staff) => (
                      <option key={staff.staffId} value={staff.staffId}>
                        {staff.staffNm} ({staff.deptNm || '소속없음'})
                      </option>
                    ))}
                  </select>
                  {isLoadingStaff ? (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 animate-spin" />
                  ) : (
                    <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  )}
                </div>
              </div>
            </div>

            {/* 연락처 / 이메일 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">연락처 (숫자만 입력)</label>
                <input
                  type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="01012345678"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">이메일</label>
                <input
                  type="email" name="email" value={formData.email} onChange={handleChange} placeholder="name@safety.co.kr"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* 주소 입력 영역 */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-xs font-semibold text-slate-500 mb-2">주소 정보</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input 
                    type="text" name="zipcode" readOnly value={formData.zipcode} placeholder="우편번호" 
                    className="h-10 w-28 rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:outline-none" 
                  />
                  <button 
                    type="button" onClick={openAddressSearch} 
                    className="flex items-center gap-1.5 h-10 px-3 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600 text-sm font-medium hover:bg-indigo-100 transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> 주소 검색
                  </button>
                </div>
                <input 
                  type="text" name="address1" readOnly value={formData.address1} placeholder="기본 주소 (주소 검색을 이용하세요)" 
                  className="h-10 w-full rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:outline-none" 
                />
                <input 
                  type="text" name="address2" value={formData.address2} onChange={handleChange} placeholder="상세 주소를 입력하세요 (예: 101동 202호)" 
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" 
                />
              </div>
            </div>
            
            {/* 상태 수정 (수정 모드에만 보임) */}
            {type === "edit" && (
              <div className="pt-2 border-t border-slate-100">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">계정 상태</label>
                <div className="relative max-w-[200px]">
                  <select name="status" value={formData.status} onChange={handleChange} className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    <option value="A">정상 (활성)</option>
                    <option value="B">정지 (잠금)</option>
                    <option value="C">삭제 (탈퇴)</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 bg-slate-50 shrink-0">
            <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-300 bg-white px-5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              취소
            </button>
            <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 h-10 rounded-lg bg-indigo-600 px-5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {type === "add" ? "등록하기" : "저장하기"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}